/** @format */

import { ref } from "vue";
import { FREE_SHIPPING_MIN_SUBTOTAL } from "@outstanding/shared";

/**
 * Free-ground-shipping eligibility threshold FALLBACK, in integer cents.
 *
 * The value is @outstanding/shared's FREE_SHIPPING_MIN_SUBTOTAL (bead 49n.3b) —
 * the SAME constant the API enforces at PUT /cart/:id/shipping-speed and at
 * checkout, re-exported here so it can't drift from the server. GET
 * /api/shipping-options serves the live value as `freeShippingMinSubtotal`
 * (outstanding-qbo.3); this constant is only the fallback for when that fetch
 * hasn't landed (first paint) or failed.
 *
 * Single client-side source of truth: the shipping selector's FREE-option
 * gating/label AND the cart_nudge shipping-progress line (bead zpe.3) both
 * derive from getFreeShippingMinSubtotal(), so they can never disagree with
 * each other.
 */
export { FREE_SHIPPING_MIN_SUBTOTAL };

// The threshold as served by GET /api/shipping-options, when it has arrived.
// A ref (not a plain module variable) so computeds/templates reading the
// getter re-evaluate when the fetched value lands mid-session.
const servedFreeShippingMinSubtotal = ref<number | null>(null);

/**
 * Record the server-served free-shipping threshold (integer cents).
 * Fed by useCheckout's /api/shipping-options fetch. Defensive: a missing,
 * non-numeric, or negative value is IGNORED (last-good/fallback wins), so an
 * older API response or a malformed payload can never break the threshold
 * math. Passing `null` explicitly clears back to the compiled-in fallback
 * (used by specs; production code never clears).
 */
export function setFreeShippingMinSubtotal(value: unknown): void {
  if (value === null) {
    servedFreeShippingMinSubtotal.value = null;
    return;
  }
  const cents = Number(value);
  if (Number.isFinite(cents) && cents >= 0) {
    servedFreeShippingMinSubtotal.value = Math.round(cents);
  }
}

/**
 * The free-ground-shipping threshold to use for all client-side decisions:
 * the server-served value when available, else the compiled-in fallback.
 * Reactive — safe to read inside computeds/templates.
 */
export function getFreeShippingMinSubtotal(): number {
  return servedFreeShippingMinSubtotal.value ?? FREE_SHIPPING_MIN_SUBTOTAL;
}
